import { FloraVariants, styled } from '@grupoboticario/flora'
import { forwardRef, HTMLFloraProps } from '@grupoboticario/flora-react'
import React, { MouseEvent, useCallback, useContext } from 'react'
import { CarouselContext } from '../Carousel'

const StyledDot = styled('button', {
  all: 'unset',
  display: 'inline-block',
  backgroundColor: 'transparent',
  boxShadow: 'inset 0 0 0 1px $$boxShadowNormal',
  borderRadius: '$small',
  cursor: 'pointer',
  height: '$2',
  position: 'relative',
  width: '$2',
  transition: 'background-color 300ms ease-out, box-shadow 300ms ease-out, width 300ms ease-out',

  '&[data-state="on"]': {
    width: '$7',
    backgroundColor: '$$backgroundColorOn',
    boxShadow: 'none',
  },

  '&[data-state="off"]:hover': {
    backgroundColor: '$$backgroundColorHoverOff',
    boxShadow: 'inset 0 0 0 1px $$boxShadowNormalHover',
  },

  '&:focus': {
    boxShadow: '$outline',
  },

  '&::before': {
    content: '',
    height: '100%',
    left: ' 50%',
    minHeight: '$4',
    minWidth: '$6',
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    top: '50%',
    width: '100%',
  },

  variants: {
    color: {
      standard: {
        $$boxShadowNormal: '$colors$actionableDefault',
        $$boxShadowNormalHover: '$colors$actionableHover',
        $$backgroundColorOn: '$colors$disabledBackground',
        $$backgroundColorHoverOff: '$colors$actionableHover',
      },
      alt: {
        $$boxShadowNormal: '$colors$actionableAlt',
        $$boxShadowNormalHover: '$colors$actionableAlt',
        $$backgroundColorOn: '$colors$disabledAltBackground',
        $$backgroundColorHoverOff: '$colors$actionableAlt',
      },
    },
  },

  defaultVariants: {
    color: 'standard',
  },
})

export type DotProps = HTMLFloraProps<'button'> &
  FloraVariants<typeof StyledDot> & {
    index: number
    active?: boolean
    'aria-label': string
  }

export const Dot = forwardRef<'button', DotProps>(
  ({ active, index, onClick, ...props }, forwardedRef) => {
    const {
      state: { goTo, onDotPress },
    } = useContext(CarouselContext)

    const handleOnClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        goTo(index)
        onDotPress(index)
        onClick?.(event)
      },
      [goTo, index, onClick, onDotPress],
    )

    return (
      <StyledDot
        {...props}
        onClick={handleOnClick}
        role="checkbox"
        aria-checked={!!active}
        data-slide-index={index}
        data-state={!!active ? 'on' : 'off'}
        ref={forwardedRef}
      />
    )
  },
)
